import { Router } from '@angular/router';
import { ErrorResponse } from './../../../models/error.model';
import { DataTableColumn } from './../data-table/data-table.component';
import { UtilsService } from 'src/app/services/utils.service';
import { Component, Input, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { Papa } from 'ngx-papaparse';
import { BulkExecutionError } from 'src/app/models/bulk-execution-error.model';

export interface CSVColumnMap {
    key: string;
    description: string;
    fieldName?: string;
}

@Component({
    selector: 'app-bulk-creator',
    templateUrl: './bulk-creator.component.html',
    styleUrls: ['./bulk-creator.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class BulkCreatorComponent implements OnInit {

    @Input()
    columnMaps: CSVColumnMap[];

    @Input()
    getErrorsFn: (row: any) => string[];

    @Input()
    submitFn: (values: any[]) => Promise<BulkExecutionError[]>;

    fields: string[];
    data: { [key: string]: string }[];
    errors: string[][];

    parsingFile = false;
    submitted = false;

    @ViewChild('fieldEditionColumnTemplate', { static: true })
    fieldEditionColumnTemplate: TemplateRef<any>;

    @ViewChild('deleteRowColumnTemplate', { static: true })
    deleteRowColumnTemplate: TemplateRef<any>;

    constructor(
        private parser: Papa,
        private utilsService: UtilsService,
    ) { }

    ngOnInit(): void {
    }

    onFileSelection(event: Event) {
        const file = (event.target as HTMLInputElement).files[0];
        if (file) {
            this.parsingFile = true;
            const reader = new FileReader();
            reader.readAsText(file, 'ISO-8859-1'); // Verificar si siempre se usa esto o es solo mi excel
            reader.onload = () => {
                this.parseCSV(reader.result as string);
            };
        }
    }

    parseCSV(csv: string) {
        this.parser.parse(csv, {
            skipEmptyLines: true,
            header: true,
            complete: result => {
                if (result.errors.length) {
                    this.utilsService.alert('Error al procesar archivo', result.errors[0].message);
                } else {
                    this.fields = result.meta.fields;
                    this.data = result.data.filter(item => !Object.values(item).every(value => !value));
                    // Automaticamente seleccionamos un mapeo en los nombres que coinciden con la descripción del campo
                    this.fields.forEach(field => {
                        const columnMap = this.columnMaps.find(column => this.utilsService.stringMatcher(column.description, field));
                        if (columnMap) {
                            columnMap.fieldName = field;
                        }
                    });
                    // Generamos los errores por primera vez
                    this.updateErrors();
                }
                this.parsingFile = false;
            }
        });
    }

    getUnusedFields(currentColumn: CSVColumnMap) {
        return this.fields.filter(field => !this.columnMaps.find(column => column.fieldName === field && column !== currentColumn));
    }

    getDataEditorColumns() {
        const columns: DataTableColumn[] = [];
        if (this.submitted) {
            columns.push({ name: 'Resultado de ejecución', prop: 'executionStatus', cellClass: 'error-cell', minWidth: '350px' });
        }
        columns.push({ name: 'Errores', cellClass: 'error-cell', minWidth: '350px', parseValue: (value, column, index) => this.errors[index]?.length ? this.errors[index][0] : 'Sin errores' });
        this.columnMaps.forEach(column => columns.push({
            name: column.description,
            template: this.fieldEditionColumnTemplate,
            templateParams: { fieldName: column.fieldName ?? column.key },
            width: '220px'
        }));
        columns.push({ template: this.deleteRowColumnTemplate, width: '100px' });
        return columns;
    }

    mapDataValues(dataItem: { [key: string]: string }) {
        const response: any = {};
        this.columnMaps.forEach(column => {
            if (column.fieldName && dataItem[column.fieldName] !== undefined) {
                response[column.key] = dataItem[column.fieldName];
            } else {
                response[column.key] = dataItem[column.key] ?? null;
            }
        });
        return response;
    }

    updateErrors(row?: any, index?: number) {
        if (row) {
            this.errors[index] = this.getErrorsFn(this.mapDataValues(row));
        } else {
            this.errors = this.data.map(item => this.getErrorsFn(this.mapDataValues(item)));
        }
    }

    availableToSubmit() {
        return this.errors?.every(rowErrors => rowErrors.length === 0);
    }

    deleteRow(index: number) {
        this.data.splice(index, 1);
        this.errors.splice(index, 1);
    }

    runSubmit() {
        const values = this.data.map(dataItem => this.mapDataValues(dataItem));
        this.submitFn(values).then(errors => {
            let errorCount = 0;
            this.data.forEach((item, index) => {
                const error = errors.find(e => e.index === index);
                item.executionStatus = error?.error ? error.mensaje : 'COMPLETADO';
                if (error?.error) {
                    errorCount++;
                }
            });
            this.submitted = true;
            this.data = this.data.filter(item => item.executionStatus !== 'COMPLETADO');
            this.updateErrors();
            if (errorCount > 0) {
                this.utilsService.alert('Algunos errores', 'Hubo algunos errores al ejecutar, por favor revise en la tabla de verificación de campos los errores y vuelva a ejecutar.');
            } else {
                const modal = this.utilsService.alert('Ejecutado correctamente', 'Todas las consultas fueron ejecutadas correctamente');
            }
        }, (error: ErrorResponse | Error | string) => this.utilsService.alert('Error al ejecutar', typeof error === 'string' ? error : error instanceof Error ? error.message : error.error?.descripcion));
    }
}
